import { Autocomplete, Divider, Grid, Paper, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { CONTRACT_STATUSES } from '@vy/types';
import { isNotNullOrUndefined, values, vyDate } from '@vy/utils';
import React, { Suspense } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFormErrorNotificationContext } from '../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useContractsByCustomerId } from '../../../../hooks/requests/use-contracts-by-customer-id';
import type { InvoiceFormInput } from './CreateInvoiceForm/create-invoice-form-types/invoice-form-input';
import { CustomerSelect } from './CustomerSelect';
import { INVOICE_DUE_DATE_VALUES } from '../../../customers/CreateCustomerPage/create-customer-page-consts/invoice-due-date-consts';
import { VyDatePicker } from '../../../../components/new-design/date-picker';

export const CreateInvoiceSettings: React.FC = () => {
  const { control, register, setValue, watch } = useFormContext<InvoiceFormInput>();
  const { registerErrorField } = useFormErrorNotificationContext();
  const customerId = watch('settings.customerId');

  const { data: contractsByCustomer } = useContractsByCustomerId(customerId ?? '', undefined, {
    enabled: isNotNullOrUndefined(customerId),
    suspense: false,
  });

  const startPeriodRegister = register('settings.startDate');
  const endPeriodRegister = register('settings.endDate');
  const issueDateRegister = register('settings.issueDate');
  const purchaseOrderRegister = register('settings.purchaseOrder');
  const customerIdRegister = register('settings.customerId');

  registerErrorField(customerIdRegister.name);

  const getDueDateDisplayText = (value: number | string) => {
    return `Net ${value.toString() === INVOICE_DUE_DATE_VALUES.EOM.toString() ? 'EoM' : value}`;
  };

  const activeContracts =
    contractsByCustomer?.contracts.filter(({ status }) => status === CONTRACT_STATUSES.Active) ?? [];
  const contractsOptions = activeContracts.map(({ id, plan }) => ({
    label: plan.name,
    value: id,
  }));

  return (
    <section className="flex h-full flex-col p-6 gap-y-6 overflow-auto">
      <Typography variant="h6">Settings</Typography>
      <Stack direction="column" sx={{
        mb: "40px"
      }}>
        <Typography variant="overline" sx={{
          color: "text.secondary"
        }}>
          Terms
        </Typography>

        <Stack
          component={Paper}
          elevation={1}
          divider={<Divider />}
          sx={{
            rowGap: 3,
            padding: 3
          }}>
          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Customer</Typography>
            </Grid>

            <Grid size={3}>
              <Suspense
                fallback={<Skeleton variant="rectangular" height={40} sx={{ borderRadius: '12px' }} />}
              >
                <CustomerSelect />
              </Suspense>
            </Grid>
          </Grid>

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Period</Typography>
            </Grid>

            <Grid container wrap="nowrap" size={6} sx={{
              gap: 1
            }}>
              <Controller
                name={startPeriodRegister.name}
                render={({ field }) => (
                  <VyDatePicker
                    size="small"
                    label="Start Date"
                    value={field.value}
                    onChange={(newDate: Date | null) => {
                      if (isNotNullOrUndefined(newDate)) {
                        const startOfNewDate = vyDate(newDate).startOf('day').toDate();
                        setValue(startPeriodRegister.name, startOfNewDate.toISOString());
                      }
                    }}
                  />
                )}
              />
              <Controller
                name={endPeriodRegister.name}
                render={({ field }) => {
                  const startPeriodValue = watch(startPeriodRegister.name);
                  const minDate = startPeriodValue ? vyDate(startPeriodValue) : undefined;

                  return (
                    <VyDatePicker
                      size="small"
                      label="End Date"
                      value={field.value}
                      minDate={minDate}
                      onChange={(newDate: Date | null) => {
                        if (isNotNullOrUndefined(newDate)) {
                          const endOfNewDate = vyDate(newDate).endOf('day').toDate();
                          setValue(endPeriodRegister.name, endOfNewDate.toISOString());
                        }
                      }}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Issue Date</Typography>
            </Grid>

            <Grid size={3}>
              <Controller
                name={issueDateRegister.name}
                render={({ field }) => (
                  <VyDatePicker
                    size="small"
                    label="Issue Date"
                    value={field.value}
                    onChange={(newDate: Date | null) => {
                      if (isNotNullOrUndefined(newDate)) {
                        const startOfNewDate = vyDate(newDate).startOf('day').toDate();
                        setValue(issueDateRegister.name, startOfNewDate.toISOString());
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Due Date</Typography>
            </Grid>

            <Grid size={3}>
              <Controller
                control={control}
                name="settings.paymentDueDays"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    autoHighlight
                    size="small"
                    disableClearable
                    options={values(INVOICE_DUE_DATE_VALUES)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Due date"
                        slotProps={{
                          ...params.slotProps,

                          htmlInput: {
                            ...params.slotProps.htmlInput,
                            autoComplete: 'new-password',
                          }
                        }}
                      />
                    )}
                    getOptionLabel={getDueDateDisplayText}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography variant="subtitle1">PO. Number</Typography>
            </Grid>

            <Grid size={3}>
              <TextField size="small" placeholder="input text" fullWidth {...purchaseOrderRegister} />
            </Grid>
          </Grid>

          <Grid container sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <Typography>Contract</Typography>
            </Grid>

            <Grid size={3}>
              <Controller
                control={control}
                name="settings.contractId"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    autoHighlight
                    size="small"
                    disableClearable
                    options={contractsOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select contract"
                        slotProps={{
                          ...params.slotProps,

                          htmlInput: {
                            ...params.slotProps.htmlInput,
                            autoComplete: 'new-password',
                          }
                        }}
                      />
                    )}
                    getOptionLabel={(option) => option.label}
                    onChange={(_, newValue) => {
                      field.onChange(newValue.value);
                    }}
                    value={contractsOptions.find((option) => option.value === field.value)}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </section>
  );
};
